import { css } from '@emotion/css';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import { Button, BulletPoints, Headline } from '@volumental/common';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { uiConfig } from '../../../services/config';
import { useScannerStore, useSessionStore } from '../../../store';
import { type UserError } from '../../../types/types';
import { ImageCompareBox } from './image-compare-box';
import { getErrorDetailsFromUserError } from './utils';

const containerStyle = css`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const textStyle = css`
  font-weight: ${uiConfig.styles.fontWeights.bold};
  font-size: 21px;
  text-align: left;
  width: 100%;
`;

const buttonStyle = css`
  margin: 24px 0px;
  width: 100%;
`;

const listStyle = css`
  width: 100%;
  font-size: ${uiConfig.styles.fontSizes.regular};
  font-weight: ${uiConfig.styles.fontWeights.regular};
  margin-bottom: 16px;
`;

interface CompareCaptureProps {
  userError?: UserError;
  onClick: () => void;
  buttonTitle?: string;
  refImg: string;
  capturedImg?: Blob;
}

export function CompareCapture({
  userError,
  onClick,
  buttonTitle,
  refImg,
  capturedImg,
}: CompareCaptureProps): JSX.Element {
  const { t } = useTranslation();
  const { flowType } = useScannerStore((store) => store);
  const { mode } = useSessionStore((store) => store);
  const imageUrl = useMemo(
    () => capturedImg && URL.createObjectURL(capturedImg),
    [capturedImg],
  );
  const { title, bulletPoints } = getErrorDetailsFromUserError(t, userError);

  return (
    <div className={containerStyle}>
      <Headline className={textStyle}>{title}</Headline>
      <div className={listStyle}>
        <BulletPoints bulletPoints={bulletPoints} />
      </div>
      {imageUrl && (
        <ImageCompareBox
          mainImage={refImg}
          mainTitle={t('ideal')}
          mainAlt={t('imageAlt.refImage')}
          secondaryImage={imageUrl}
          secondaryTitle={t('yours')}
          secondaryAlt={t('imageAlt.yourImage')}
        />
      )}
      <div className={buttonStyle}>
        <Button
          onClick={() => {
            usageTracker.track(UsageTrackerEvent.UserTappedTryAgain, {
              sizing_flow_type: flowType,
              experience: mode,
            });
            onClick();
          }}
        >
          {buttonTitle ?? t('button.tryAgain')}
        </Button>
      </div>
    </div>
  );
}
